import { makeAutoObservable } from 'mobx';
import { MicroCycleDay, createMicroCycleDay } from './MicroCycleDay';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { ProgramParams } from './ProgramParams';
import { CoachProgramType } from './CoachProgramType';
import { Pageable } from '../../Interfaces/Pageable';

export type MicroCycleQueryRequest = Pageable & {
  athleteSuperMacroId?: string;
  startDateTime?: Date;
  endDateTime?: Date;
  mesoCycleTemplateId?: string;
};

export type MicroCycle = {
  id: string;
  athleteSuperMacroId: string;
  mesoCycleId: string;
  athleteId: string;
  microCycleTemplateId: string;
  mesoCycleTemplateId: string;
  startDateTime: Date;
  endDateTime?: Date;
  deadline?: Date;
  entries: MicroCycleDay[];
  type: CoachProgramType;
  weekIndex: number;
  isLastMicroCycle: boolean;
  readonly isFinished: boolean;
  setBasicData(json: Partial<MicroCycle>): void;
  updateData(json: Partial<MicroCycle>): void;
  refresh(params?: ProgramParams): Promise<void>;
  reload(): Promise<void>;
};

export function createMicroCycle(json?: Partial<MicroCycle>): MicroCycle {
  const cycle: MicroCycle = makeAutoObservable({
    id: json?.id ?? '',
    athleteSuperMacroId: json?.athleteSuperMacroId ?? '',
    mesoCycleId: json?.mesoCycleId ?? '',
    athleteId: json?.athleteId ?? '',
    microCycleTemplateId: json?.microCycleTemplateId ?? '',
    mesoCycleTemplateId: json?.mesoCycleTemplateId ?? '',
    startDateTime: json?.startDateTime ? new Date(json.startDateTime as any) : new Date(),
    endDateTime: json?.endDateTime ? new Date(json.endDateTime as any) : undefined,
    deadline: json?.deadline ? new Date(json.deadline as any) : undefined,
    entries: (json?.entries ?? []).map((e: any) => createMicroCycleDay(e)),
    type: json?.type ?? 'superMacro',
    weekIndex: json?.weekIndex ?? 0,
    isLastMicroCycle: json?.isLastMicroCycle ?? false,

    get isFinished(): boolean {
      return this.entries.every((e) => e.isFinished);
    },

    setBasicData(json: Partial<MicroCycle>) {
      this.id = json.id ?? '';
      this.athleteSuperMacroId = json.athleteSuperMacroId ?? '';
      this.mesoCycleId = json.mesoCycleId ?? '';
      this.athleteId = json.athleteId ?? '';
      this.microCycleTemplateId = json.microCycleTemplateId ?? '';
      this.mesoCycleTemplateId = json.mesoCycleTemplateId ?? '';
      this.startDateTime = json.startDateTime ? new Date(json.startDateTime as any) : new Date();
      this.endDateTime = json.endDateTime ? new Date(json.endDateTime as any) : undefined;
      this.deadline = json.deadline ? new Date(json.deadline as any) : undefined;
      this.type = json.type ?? 'superMacro';
      this.weekIndex = json.weekIndex ?? 0;
      this.isLastMicroCycle = json.isLastMicroCycle ?? false;
    },

    updateData(json: Partial<MicroCycle>) {
      this.setBasicData(json);
      ((json.entries as any) ?? []).map((e: any, newIndex: number) => {
        const entry = this.entries[newIndex];
        if (entry && entry.id === e.id) {
          entry.updateData(e);
        } else {
          this.entries[newIndex] = createMicroCycleDay(e);
        }
        const index = this.entries.findIndex((e1) => e1.id === e.id);
        if (index !== -1) {
          const existingEntry = this.entries[index];
          existingEntry.updateData(e);
        }
      });
    },

    async refresh(params?: ProgramParams) {
      const result =
        this.type === 'program'
          ? await HttpBackend.post(
              `/coach/program/${this.athleteSuperMacroId}/cycle/${this.id}/refresh`,
              params,
            )
          : await HttpBackend.post(`/coach/${this.athleteSuperMacroId}/cycle/${this.id}/refresh`, params);
      if (result) {
        this.updateData(result);
      }
    },

    async reload() {
      const res = await HttpBackend.get(`/coach/micro-cycle/${this.id}`);
      if (res) {
        this.updateData(res);
      }
    },
  });

  return cycle;
}

export const MicroCycleApi = {
  find(params?: MicroCycleQueryRequest): Promise<MicroCycle[]> {
    return HttpBackend.get('/coach/micro-cycle', params).then((result) => result.map((w: any) => createMicroCycle(w)));
  },

  get(cycleId: string): Promise<MicroCycle> {
    return HttpBackend.get(`/coach/micro-cycle/${cycleId}`).then((res) => createMicroCycle(res));
  },

  current(athleteId?: string): Promise<MicroCycle> {
    return HttpBackend.get('/coach/micro-cycle/current', { athleteId }).then((result) => createMicroCycle(result));
  },

  previous(athleteId?: string): Promise<MicroCycle | undefined> {
    return HttpBackend.get('/coach/micro-cycle/previous', { athleteId }).then((result) =>
      result ? createMicroCycle(result) : undefined,
    );
  },
};
